package com.yogaaicoach.pages.dashboard

import com.yogaaicoach.common.ui.components.LoadingPage
import com.yogaaicoach.common.ui.components.TopBar
import com.yogaaicoach.pages.auth.TokenDataManager
import com.yogaaicoach.scope
import com.yogaaicoach.user.AppViewModel
import kotlinx.coroutines.launch
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.useEffectOnce
import react.useState
import web.cssom.ClassName

internal external interface DashboardPageProps : Props {
    var viewModel: DashboardViewModel
    var appViewModel: AppViewModel
    var onAddNewTraining: () -> Unit
    var onNavigateToInsights: () -> Unit
    var onNavigateToPayment: () -> Unit
    var onNavigateToContactUs: () -> Unit
    var onNavigateToWorkout: (workoutId: String, duration: Int, isCompleted: Boolean) -> Unit
    var onNavigateToUpdateTraining: () -> Unit
    var onNavigateToTest: () -> Unit
}

// Every DashboardMode now has a ported page: NewUser -> OnboardingPage, Ready ->
// ProgramDashboardPage, Locked -> LockedPage, NoActiveProgram -> NoActiveProgramPage,
// TrainingCreationInProgress -> LoadingPage.
internal val DashboardPage = FC<DashboardPageProps> { props ->
    var isPageLoaded by useState(false)
    var isTrainingCreationInProgress by useState(false)
    var dashboardItems by useState(emptyList<DashboardItem>())
    var dashboardData by useState<DashboardData?>(null)
    var nextWorkout by useState<DashboardItem?>(null)

    useEffectOnce {
        scope.launch { props.viewModel.isPageLoaded.collect { isPageLoaded = it } }
        scope.launch { props.viewModel.isTrainingCreationInProgress.collect { isTrainingCreationInProgress = it } }
        scope.launch { props.viewModel.dashboardItems.collect { dashboardItems = it } }
        scope.launch { props.viewModel.dashboardData.collect { dashboardData = it } }
        scope.launch { props.viewModel.nextWorkout.collect { nextWorkout = it } }
        props.viewModel.loadPage(props.appViewModel::askNewToken)
    }

    val mode = if (isPageLoaded) {
        resolveDashboardMode(dashboardItems, dashboardData?.hasPreviousProgram ?: false, isTrainingCreationInProgress, TokenDataManager.tokenData)
    } else {
        null
    }

    TopBar {
        title = if (mode == DashboardMode.NewUser) "Welcome" else "Dashboard"
        onSignOut = { props.appViewModel.signOut() }
        onNavigateToDashboard = null
        onNavigateToInsights = props.onNavigateToInsights
        onNavigateToPayment = props.onNavigateToPayment
        onNavigateToContactUs = props.onNavigateToContactUs
    }

    // LoadingScreen()/SubscriptionLockedOverlay are both fillMaxSize(), unconstrained by
    // PageLayout's 1000dp cap, since neither one calls PageLayout — they replace the whole
    // dashboard content area instead of living inside it, so both sit outside
    // .dashboard-page-shell. DashboardScreen.kt calls the exact same LoadingScreen() composable
    // for both the "page not loaded yet" state and DashboardMode.TrainingCreationInProgress.
    if (mode == null || mode == DashboardMode.TrainingCreationInProgress) {
        LoadingPage {}
    } else if (mode == DashboardMode.Locked) {
        LockedPage {
            onUpgradeClicked = props.onNavigateToPayment
        }
    } else {
        div {
            className = ClassName("dashboard-page-shell")

            when (mode) {
                DashboardMode.NewUser -> OnboardingPage {
                    onAddNewTraining = props.onAddNewTraining
                    onNavigateToTest = props.onNavigateToTest
                }
                DashboardMode.Ready -> ProgramDashboardPage {
                    this.dashboardData = dashboardData!!
                    this.nextWorkout = nextWorkout
                    onStartWorkout = props.onNavigateToWorkout
                    onNavigateToUpdateTraining = props.onNavigateToUpdateTraining
                    onNavigateToTest = props.onNavigateToTest
                }
                DashboardMode.NoActiveProgram -> NoActiveProgramPage {
                    this.dashboardData = dashboardData!!
                    onAddNewTraining = props.onAddNewTraining
                    onNavigateToTest = props.onNavigateToTest
                }
            }
        }
    }
}
